/**
 * FastProgramExplainer — owner-facing Fast by NoFee explainer.
 *
 * iter134 (Jul 2026) — Owner spec: retire public tier ladder
 * (Standard / Plus / Premium / Thunder). Fast Score remains an
 * internal operational metric visible only inside the restaurant
 * dashboard and setup wizard. This explainer now describes the
 * program as Verified On-Time Delivery — how points are earned,
 * how customers verify each delivery, and what Fast by NoFee
 * unlocks for the restaurant.
 *
 * Rendered in TWO places:
 *   1. Restaurant onboarding — Fast by NoFee setup section (light).
 *   2. Restaurant dashboard  — Fast performance panel (dark).
 */
import React from 'react';
import { Zap, CheckCircle2 } from 'lucide-react';

const BENEFITS = [
  'Increase customer confidence',
  'Improve restaurant credibility',
  'Highlight reliable delivery performance',
  'Encourage repeat customers',
  'Strengthen customer trust',
];

const FastProgramExplainer = ({ variant = 'dark', testid = 'fast-program-explainer' }) => {
  const isLight = variant === 'light';
  const surface = isLight
    ? 'bg-white border-slate-200 text-slate-800'
    : 'bg-zinc-950 border-zinc-800 text-zinc-200';
  const headingCls = isLight ? 'text-slate-900' : 'text-white';
  const subtleCls = isLight ? 'text-slate-500' : 'text-zinc-400';
  const rowTextCls = isLight ? 'text-slate-700' : 'text-zinc-300';

  return (
    <div className={`rounded-2xl border p-6 ${surface}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-5 h-5 text-yellow-400 fill-yellow-300" />
        <h3 className={`text-base font-bold ${headingCls}`} data-testid={`${testid}-title`}>
          Fast by NoFee — Verified On-Time Delivery
        </h3>
      </div>
      <p className={`text-sm leading-relaxed ${subtleCls} mb-4`} data-testid={`${testid}-intro`}>
        Fast by NoFee is a trust program. Restaurants earn recognition by consistently delivering orders within the promised delivery time. Delivery confirmation comes directly from real customers after each completed order.
      </p>

      {/* Scoring — Fast Score stays as an internal operational metric */}
      <div>
        <p className={`text-[11px] uppercase tracking-[0.18em] font-bold mb-2 ${subtleCls}`}>
          How your Fast Score is calculated
        </p>
        <ul className="space-y-1.5 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-base leading-none mt-0.5">👍</span>
            <span className={rowTextCls}>
              <span className={`font-semibold ${headingCls}`}>On-Time Delivery</span> = <span className="text-emerald-600 font-bold">+1 Fast Point</span>
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-base leading-none mt-0.5">👎</span>
            <span className={rowTextCls}>
              <span className={`font-semibold ${headingCls}`}>Late delivery confirmed by customer</span> = <span className="text-rose-600 font-bold">-5 Fast Points</span>
            </span>
          </li>
        </ul>
        <p className={`mt-2 text-[12px] ${subtleCls} leading-snug`}>
          We never automatically penalize you. Points are only deducted when a customer explicitly confirms the delivery arrived late. Fast Score is an internal metric — customers only see whether your restaurant is Fast by NoFee, not the underlying score.
        </p>
      </div>

      {/* Benefits */}
      <div className="mt-5">
        <p className={`text-[11px] uppercase tracking-[0.18em] font-bold mb-2 ${subtleCls}`}>
          Benefits of Fast by NoFee
        </p>
        <ul className="grid sm:grid-cols-2 gap-1.5 text-sm">
          {BENEFITS.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
              <span className={`leading-snug ${rowTextCls}`}>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer highlight */}
      <p className={`mt-5 text-[12.5px] ${subtleCls} leading-relaxed`}>
        <span className={`font-semibold ${headingCls}`}>Fast by NoFee does not mean the fastest delivery.</span> It means: Delivered On Time. Verified by Real Customers.
      </p>
    </div>
  );
};

export default FastProgramExplainer;
